/**
 * Win and loss streaks over a manager's games. A streak never crosses a season
 * boundary (a win in week 17 and another in week 1 are two streaks of one),
 * and a tie ends one without starting another.
 */

import type { GameResult, PersonGame } from "../reportBuilder/ledger";

export interface Streak {
  length: number;
  season: number;
  startWeek: number;
  endWeek: number;
}

export interface StreakSummary {
  longestWin: Streak | null;
  longestLoss: Streak | null;
  /**
   * The run the manager is on in the latest season they played; null when
   * their last game was a tie or there are no games.
   */
  current: { result: "W" | "L"; length: number; season: number } | null;
}

/** `games` must be one manager's, oldest first (the ledger's order). */
export function summarizeStreaks(games: readonly PersonGame[]): StreakSummary {
  const longest: Record<"W" | "L", Streak | null> = { W: null, L: null };
  // Cast so the loop's assignments, not the initial null, decide the type.
  let run = null as Streak | null;
  let runResult = null as GameResult | null;

  for (const game of games) {
    if (run && game.result === runResult && game.season === run.season) {
      run = { ...run, length: run.length + 1, endWeek: game.week };
    } else {
      runResult = game.result;
      run = { length: 1, season: game.season, startWeek: game.week, endWeek: game.week };
    }
    // Strictly longer, so the earliest of equal streaks stands.
    if (runResult !== "T" && run.length > (longest[runResult!]?.length ?? 0)) longest[runResult!] = run;
  }

  return {
    longestWin: longest.W,
    longestLoss: longest.L,
    current: run && runResult && runResult !== "T" ? { result: runResult, length: run.length, season: run.season } : null,
  };
}

/** Longer first; equal lengths go to whichever ended last. */
export function compareStreaks(a: Streak, b: Streak): number {
  return b.length - a.length || b.season - a.season || b.endWeek - a.endWeek;
}
